import type { CandidateAgreement, TemplateVersion } from "@/lib/api";

interface AgreementPreviewProps {
  agreement?: CandidateAgreement;
  candidate: {
    email: string;
    fullName: string;
    phone: string;
    submittedAt?: string;
  };
  showExecutionBlock?: boolean;
  templateVersion?: TemplateVersion;
}

const DEFAULT_JURISDICTION_CITY = "Mysore";

function valueOrFallback(value: unknown, fallback: string) {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : fallback;
}

function formatAgreementDate(value?: string) {
  if (!value) {
    return new Date().toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric"
    });
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "long",
    year: "numeric"
  });
}

function replaceTokens(template: string, values: Record<string, string>) {
  return template.replace(/\{\{(.*?)\}\}/g, (_match, key) => values[key.trim()] ?? "");
}

function resolveAgreementText(
  value: string | undefined,
  fallback: string,
  tokens: Record<string, string>
) {
  const source = typeof value === "string" && value.trim().length > 0 ? value : fallback;

  return fillLegacyBlanks(replaceTokens(source, tokens), tokens)
    .replace(/\r\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function formatStipendAmount(value: string | undefined) {
  if (!value) {
    return "To be confirmed";
  }

  const digitsOnly = value.replace(/\D/g, "");

  if (!digitsOnly) {
    return value;
  }

  return `₹${new Intl.NumberFormat("en-IN").format(Number(digitsOnly))} per month`;
}

export function AgreementPreview({
  agreement,
  candidate,
  showExecutionBlock = true,
  templateVersion
}: AgreementPreviewProps) {
  const signatureDate =
    typeof agreement?.signedAt === "string" ? agreement.signedAt : candidate.submittedAt;
  const agreementDate = formatAgreementDate(signatureDate ?? agreement?.agreementDate);
  const startDate = formatAgreementDate(agreement?.startDate);
  const endDate = formatAgreementDate(agreement?.endDate);
  const documentTitle = valueOrFallback(
    templateVersion?.title,
    "Internship Agreement (India)"
  );
  const isAddendum = documentTitle.toLowerCase().includes("addendum");
  const usesEmbeddedDocumentLayout = isAddendum;
  const companyName = valueOrFallback(
    templateVersion?.companyName,
    "Gupio Services Private Limited"
  );
  const companyAddress = valueOrFallback(
    agreement?.companyAddress ?? templateVersion?.companyAddress,
    "Company address to be confirmed"
  );
  const candidateName = valueOrFallback(agreement?.internName, candidate.fullName);
  const candidateAddress = valueOrFallback(agreement?.internAddress, "Address to be confirmed");
  const candidateRoleLabel = isAddendum ? "Employee" : "Intern";
  const cityState = valueOrFallback(agreement?.cityState, "To be confirmed");
  const confidentialityPeriod = valueOrFallback(
    agreement?.confidentialityPeriod,
    "To be confirmed by the company"
  );
  const isUnpaid = agreement?.internshipType === "unpaid";
  const stipendAmountDisplay = isUnpaid
    ? "N/A (Unpaid Internship)"
    : formatStipendAmount(agreement?.stipendAmount);
  const workingHours = valueOrFallback(agreement?.workingHours, "To be confirmed");
  const jurisdictionCity = valueOrFallback(
    agreement?.jurisdictionCity,
    DEFAULT_JURISDICTION_CITY
  );
  const executionDate = formatAgreementDate(signatureDate ?? agreement?.agreementDate);
  const tokenValues = {
    agreementDate,
    cityState,
    companyAddress,
    companyName,
    confidentialityPeriod,
    email: candidate.email,
    endDate,
    authorizedSignatoryName: valueOrFallback(
      (agreement as any)?.authorizedSignatoryName,
      ""
    ),
    authorizedSignatoryDesignation: valueOrFallback(
      (agreement as any)?.authorizedSignatoryDesignation,
      ""
    ),
    internAddress: candidateAddress,
    internEmail: candidate.email,
    internName: candidateName,
    jurisdictionCity,
    phone: candidate.phone,
    signatureName: valueOrFallback(agreement?.signatureName, candidateName),
    signatureLocation: valueOrFallback(agreement?.signatureLocation, cityState),
    startDate,
    stipendAmountDisplay,
    title: documentTitle,
    workingHours
  };

  let agreementBodyRaw = templateVersion?.agreementBody;
  if (isUnpaid && agreementBodyRaw) {
    agreementBodyRaw = agreementBodyRaw
      .replace("This is a paid internship.", "This is an unpaid internship.")
      .replace("The Intern shall receive a stipend of {{stipendAmountDisplay}} per month.", "No stipend shall be paid for this internship.");
  }

  const agreementBody = resolveAgreementText(
    agreementBodyRaw,
    "This agreement content is not available yet.",
    tokenValues
  );
  const termsAndConditions = resolveAgreementText(
    templateVersion?.termsAndConditions,
    "Terms and conditions for this document are not available.",
    tokenValues
  );
  const dataProtectionNotice = resolveAgreementText(
    templateVersion?.dataProtectionNotice,
    "Data protection notice for this document is not available.",
    tokenValues
  );

  return (
    <div className="document-surface overflow-hidden" data-agreement-preview-root="true">
      <div className="px-6 py-8 md:px-10 lg:px-12">
        <div className="legal-page max-w-[860px]">
          {usesEmbeddedDocumentLayout ? null : (
            <section className="pt-2">
              <h1 className="text-center text-[1.9rem] font-bold uppercase tracking-[0.02em] text-ink md:text-[2.2rem]">
                {documentTitle}
              </h1>

              <p className="mt-8 text-[1.05rem] leading-9 text-ink">
                This Agreement ("Agreement") is made on <strong>{agreementDate}</strong>, at{" "}
                <strong>{cityState}</strong>.
              </p>

              <div className="legal-prose mt-8">
                <p className="text-[1.05rem] font-semibold uppercase tracking-[0.06em] text-ink">
                  Between
                </p>
                <p className="text-[1.05rem] leading-9">
                  <strong>{companyName}</strong>, a company incorporated under the provisions of the
                  Companies Act, 2013, having its registered office at{" "}
                  <strong>{companyAddress}</strong> (hereinafter referred to as the{" "}
                  <strong>"Company"</strong>, which expression shall, unless repugnant to the
                  context, include its successors and assigns),
                </p>

                <p className="pt-2 text-[1.05rem] font-semibold uppercase tracking-[0.06em] text-ink">
                  And
                </p>
                <p className="text-[1.05rem] leading-9">
                  <strong>{candidateName}</strong>, residing at <strong>{candidateAddress}</strong>{" "}
                  (hereinafter referred to as the <strong>"{candidateRoleLabel}"</strong>).
                </p>

                <p className="text-[1.05rem] leading-9">
                  The Company and the {candidateRoleLabel} shall collectively be referred to as the{" "}
                  <strong>"Parties"</strong>.
                </p>
              </div>
            </section>
          )}

          <section className={usesEmbeddedDocumentLayout ? "space-y-8" : "mt-10 space-y-8"}>
            <section>
              <DocumentParagraphs value={agreementBody} />
            </section>

            <DocumentSection title="Terms and Conditions">
              <DocumentParagraphs value={termsAndConditions} />
            </DocumentSection>

            <DocumentSection title="Data Protection Notice">
              <DocumentParagraphs value={dataProtectionNotice} />
            </DocumentSection>

            {usesEmbeddedDocumentLayout ? null : (
              <section className="legal-prose pt-2">
                <p className="text-[1.02rem] font-semibold uppercase tracking-[0.06em] text-ink">
                  In Witness Whereof
                </p>
                <p className="mt-3 text-[1.02rem] leading-8 text-ink">
                  The Parties have executed this Agreement on the date first mentioned above.
                </p>
              </section>
            )}
          </section>

          {showExecutionBlock && !usesEmbeddedDocumentLayout ? (
            <section className="mt-12">
              <ExecutionSignatureBlock
                entries={[
                  {
                    label: "Name",
                    value: valueOrFallback(agreement?.signatureName, candidate.fullName)
                  },
                  {
                    label: "Signature",
                    signatureImage:
                      typeof agreement?.signatureDataUrl === "string" && agreement.signatureDataUrl.length > 0
                        ? agreement.signatureDataUrl
                        : typeof (agreement as any)?.signatureImageData === "string" && (agreement as any).signatureImageData.length > 0
                          ? (agreement as any).signatureImageData
                          : undefined,
                    value: ""
                  },
                  {
                    label: "Date",
                    value: executionDate
                  }
                ]}
                title={candidateRoleLabel}
              />
            </section>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function DocumentParagraphs({ value }: { value: string }) {
  const paragraphs = value
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  return (
    <div className="legal-prose space-y-4">
      {paragraphs.map((paragraph, index) => (
        <p className="whitespace-pre-line text-[1.02rem] leading-9 text-ink" key={`${paragraph}-${index}`}>
          {paragraph}
        </p>
      ))}
    </div>
  );
}

function DocumentSection({
  children,
  title
}: {
  children: React.ReactNode;
  title: string;
}) {
  return (
    <section>
      <p className="text-[1.05rem] font-semibold uppercase tracking-[0.06em] text-ink">{title}</p>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function ExecutionSignatureBlock({
  entries,
  title
}: {
  entries: Array<{
    label: string;
    signatureImage?: string;
    value: string;
  }>;
  title: string;
}) {
  return (
    <section className="border-t border-line/70 pt-5">
      <p className="text-[1rem] font-semibold text-ink">{title}</p>

      <div className="mt-6 space-y-6">
        {entries.map((entry) => (
          <div className="pb-3" key={entry.label}>
            <p className="text-sm font-medium text-ink">{entry.label}:</p>
            {entry.signatureImage ? (
              <div className="mt-4 min-h-[56px]">
                <img
                  alt={`${title} signature`}
                  className="max-h-[48px] w-auto object-contain"
                  src={entry.signatureImage}
                />
              </div>
            ) : (
              <p className="mt-2 text-base font-medium text-ink">
                {entry.value || "Pending"}
              </p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function formatDayMonthYear(value: string) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  const day = parsed.toLocaleDateString("en-IN", { day: "2-digit" });
  const month = parsed.toLocaleDateString("en-IN", { month: "long" });
  const year = parsed.toLocaleDateString("en-IN", { year: "numeric" });
  return { day, month, year };
}

function fillLegacyBlanks(template: string, tokens: Record<string, string>) {
  let result = template;

  // Common addendum format: "this ___ day of ______, 20__"
  const dateParts = formatDayMonthYear(tokens.agreementDate);
  if (dateParts) {
    result = result.replace(
      /\bthis\s+_+\s+day\s+of\s+_+,\s*20_+\b/gi,
      `this ${dateParts.day} day of ${dateParts.month}, ${dateParts.year}`
    );
  }

  // Replace long underscore blanks after common phrases.
  if (tokens.companyAddress) {
    result = result.replace(
      /(registered office at)\s+_+/gi,
      (_match, prefix) => `${prefix} ${tokens.companyAddress}`
    );
  }

  if (tokens.internName) {
    result = result.replace(
      /(Mr\.?\/Ms\.?)\s+_+/gi,
      (_match, prefix) => `${prefix} ${tokens.internName}`
    );
  }

  if (tokens.internAddress) {
    result = result.replace(
      /(residing at)\s+_+/gi,
      (_match, prefix) => `${prefix} ${tokens.internAddress}`
    );
  }

  return result;
}
